import SEO from '@/components/SEO';
import { motion } from 'framer-motion';
import TrackedBookingLink from '@/components/TrackedBookingLink';
import { CONTACT_INFO } from '@/lib/constants';
import { ShieldCheck } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const RATES = [
  { id: 1, label: 'ULTIMATE', title: 'Unlimited', price: '$195', period: 'Monthly', description: 'Total access to all classes and facilities.' },
  { id: 2, label: 'CORE', title: 'Twice Weekly', price: '$185', period: 'Monthly', description: 'Two technical sessions per week.' },
  { id: 3, label: 'BASIC', title: 'Once Weekly', price: '$145', period: 'Monthly', description: 'One specialized session per week.' },
  { id: 4, label: 'FLEXIBLE', title: 'Drop In', price: '$45', period: 'Per Class', description: 'Single class access for visiting practitioners.' },
];

export default function Pricing() {
  return (
    <div className="bg-brand-black min-h-screen">
      <SEO
        title="Delta BJJ Pricing & Membership Rates | Culver City"
        description="View Delta BJJ membership options: Unlimited, Twice Weekly, Once Weekly, and Drop-In rates. Flexible Gi & No-Gi training in Culver City."
        url="https://www.deltabjj.com/pricing"
      />

      {/* Header */}
      <section className="relative pt-36 md:pt-48 pb-12 md:pb-16 px-6 lg:px-12 border-b border-white/10 text-center">
        <FadeInWhenVisible>
          <h1 className="text-4xl md:text-7xl font-display font-bold text-white tracking-tighter leading-tight mb-4">
            Membership <span className="blue-gradient-text">Pricing</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-base md:text-lg">
            Premium access for those who demand excellence in their training and environment.
          </p>
        </FadeInWhenVisible>
      </section>

      {/* Rates Grid */}
      <section className="py-12 md:py-20 px-6 lg:px-12 bg-gradient-to-b from-white via-gray-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {RATES.map((r, i) => (
              <FadeInWhenVisible key={r.id} delay={i * 0.1}>
                <div className="bg-white border-2 border-gray-200 rounded-lg shadow-sm hover:shadow-xl hover:border-brand-blue/50 transition-all duration-300 h-full flex flex-col relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-blue via-brand-blue/80 to-brand-blue/60"></div>
                  <div className="p-6 md:p-8 flex flex-col flex-grow">
                    <span className="text-brand-blue font-display text-[10px] tracking-[0.3em] uppercase mb-4 block font-bold">{r.label}</span>
                    <h3 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tighter leading-tight">{r.title}</h3>
                    <div className="mb-6 flex items-baseline">
                      <span className="text-4xl md:text-5xl font-display font-bold leading-none">{r.price}</span>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2 whitespace-nowrap">{r.period}</span>
                    </div>
                    <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow">
                      {r.description}
                    </p>
                    <TrackedBookingLink href="/#book-trial" className="btn-premium-blue w-full text-center mt-auto">
                      Join the Family
                    </TrackedBookingLink>
                  </div>
                </div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-6 lg:px-12 text-center bg-gradient-to-br from-brand-blue/10 via-white to-brand-blue/5 relative overflow-hidden border-t-4 border-brand-blue/20">
        <FadeInWhenVisible>
          <div className="max-w-4xl mx-auto relative z-10">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-bold text-brand-black mb-6 leading-none tracking-tighter">
              Ready to <span className="text-brand-blue">Start?</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-6">
              <TrackedBookingLink href="/#book-trial" className="btn-premium-blue text-base px-8 py-4 font-extrabold">
                Book 2 Free Trials
              </TrackedBookingLink>
              <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="btn-premium-outline-dark text-base px-8 py-4 font-extrabold flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-brand-blue" />
                <span>Call Us Today</span>
              </a>
            </div>
          </div>
        </FadeInWhenVisible>
      </section>

      <div className="h-1 bg-gradient-to-r from-transparent via-brand-blue/30 to-transparent"></div>
    </div>
  );
}
